import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import type * as Leaflet from 'leaflet';
import { ApiService } from '../../services/api.service';
import { CartService } from '../../services/cart.service';
import { LanguageService } from '../../services/language.service';
import {
  DeliveryLocation,
  DeliveryQuote,
  DeliverySettings,
  LatLng,
  parseDeliverySettings,
  parseLatLng,
  samePin,
} from '../../shared/order-delivery';
import { geocodeAddress, loadLeaflet, reverseGeocode } from '../../shared/delivery-map';
import { CountryCode, COUNTRIES } from '../../shared/country-code.interface';
import { hasHomeDelivery, hasInStorePickup } from '../../shared/ordering-eligibility';
import {
  PaymentFulfilment,
  PaymentMethodCopy,
  commitLabelKey,
  paymentMethodCopy,
} from '../../shared/payment-method-display';
import {
  TIP_PAYMENT_METHOD,
  TipChoice,
  TipSettings,
  parseTipSettings,
  sameTipChoice,
  tipChoiceOf,
} from '../../shared/order-tip';

export interface DeliveryFormData {
  deliveryType: 'delivery' | 'pickup';
  deliveryAddress: string;
  pickupLocationId: string;
  phone: string;
  phoneNumber: string;
  countryCode: string;
  communicationPreference: 'phone' | 'whatsapp' | 'viber' | 'telegram';
  paymentMethod?: string;
  pickupLocationName?: string;
  pickupLocationAddress?: string;
}

@Component({
    selector: 'app-delivery-form',
    templateUrl: './delivery-form.component.html',
    styleUrls: ['./delivery-form.component.scss'],
    standalone: false
})
export class DeliveryFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() clientData: any;
  @Input() pickupLocations: any[] = [];
  @Input() deliveryData: any = {};
  @Input() isAddressReadOnly: boolean = false;
  /** Why the server refused the last submit as a delivery (`delivery_*`), or null. */
  @Input() submitRefusal: string | null = null;
  @Input() initialData?: {
    deliveryType?: 'delivery' | 'pickup';
    deliveryAddress?: string;
    pickupLocationId?: string;
    phoneNumber?: string;
    countryCode?: string;
    communicationPreference?: 'phone' | 'whatsapp' | 'viber' | 'telegram';
  };

  @Output() formSubmit = new EventEmitter<DeliveryFormData>();
  @Output() formCancel = new EventEmitter<void>();

  // Form state
  // null = no selection yet. When both delivery and pickup are available we leave this
  // unselected so the user must choose (matches iOS OutsideOrderDetailsViewModel).
  deliveryType: 'delivery' | 'pickup' | null = null;
  // Set once the user explicitly taps a delivery-type button, so reactive re-runs of
  // autoSelectDeliveryType() never override an explicit choice.
  userHasChosenType: boolean = false;
  deliveryAddress: string = '';
  pickupLocationId: string = '';
  phone: string = '';
  phoneNumber: string = '';
  selectedCountryCode: CountryCode = COUNTRIES[0];
  countries: CountryCode[] = COUNTRIES;
  filteredCountries: CountryCode[] = COUNTRIES;
  isCountrySelectorOpen: boolean = false;
  countrySearchQuery: string = '';
  communicationPreference: 'phone' | 'whatsapp' | 'viber' | 'telegram' = 'phone';
  isCommunicationDropdownOpen: boolean = false;
  paymentMethod: string = '';
  phoneError: string = '';
  isPhoneValid: boolean = false;
  addressError: string = '';
  pickupLocationError: string = '';
  currentLang: string = '';

  // ── Delivery pricing ────────────────────────────────────────────────────
  //
  // Only when the venue prices delivery (`clientData.delivery.enabled`). Otherwise none of
  // this renders or runs, and the sheet is exactly what it was.
  //
  // The address stays a free-text field — the courier reads it. The pin comes from it on
  // its own: once the guest stops typing, the address is geocoded (one Nominatim lookup) and
  // the result is sent straight away as a normal cart update, so the server prices it. The
  // map is only for fine-tuning ("Adjust on map"), or for setting the pin by hand when the
  // address could not be found. The server's answer (fee, total, out of range / below
  // minimum) comes back through CartService, and Place order waits for it.
  //
  // The two directions are deliberately asymmetric, as on iOS: we rely on the coordinates,
  // and the text is for the courier. Text → pin happens on its own; pin → text only when the
  // guest moves the pin on the map, and only on Confirm, when the address at the pin
  // REPLACES the typed text (so the pin is then "for" that text and is not looked up again).

  deliverySettings: DeliverySettings = parseDeliverySettings(null);
  /** The pin, or null while there is none for the address yet. */
  deliveryPin: LatLng | null = null;
  /**
   * The address the pin was made for. A different address on screen means the pin is stale
   * and is looked up again; the same one is never looked up twice.
   */
  private pinAddress: string | null = null;
  /**
   * Where the pin came from. Only a `geocoded` one is a guess the guest has not seen — that
   * is the one an out-of-range answer suggests adjusting.
   */
  pinSource: 'geocoded' | 'manual' | 'remembered' | 'server' | null = null;
  /** Nominatim found nothing, so the pin starts at the venue and the map is required. */
  addressNotFound: boolean = false;
  isGeocoding: boolean = false;
  /** The map is shown: the guest asked to adjust the pin, or the address was not found. */
  isMapOpen: boolean = false;
  /**
   * The pin and address that went with the last location sent. Confirmation itself comes
   * from the server (its location matches the pin); these catch the guest editing the text
   * after, and tell a pin the guest already sent (re-sent to a new cart) from one they are
   * still dragging (sent only on Confirm).
   */
  private sentAddress: string | null = null;
  private sentPin: LatLng | null = null;
  deliveryQuote: DeliveryQuote | null = null;
  deliveryFee: number | null = null;
  orderTotal: number = 0;
  isPricingDelivery: boolean = false;
  deliveryErrorKey: string = '';
  currencySymbol: string = '';
  /** The last pin set for an address, from this browser's storage — reused while the address matches. */
  private rememberedPin: DeliveryLocation | null = null;
  /** The order's own location, read from the server when the sheet opens (see openForDelivery()). */
  private serverLocation: Promise<DeliveryLocation | null> = Promise.resolve(null);
  private autoLocateTimer?: ReturnType<typeof setTimeout>;

  /** The guest moved the pin on the map since it was opened (drag, tap or Back to venue). */
  pinMovedOnMap: boolean = false;
  /** The address at the moved pin ("Vitosha 12, Sofia"); replaces the typed text on Confirm. */
  mapAddress: string | null = null;
  isReverseGeocoding: boolean = false;
  private reverseTimer?: ReturnType<typeof setTimeout>;
  /** Bumped on every move, so an older lookup that lands late is ignored. */
  private reverseToken = 0;
  private subscriptions: Subscription[] = [];

  // ── Tip ─────────────────────────────────────────────────────────────────
  //
  // Tips are for online payment only — the server drops the tip from an order paid any
  // other way — so the picker (app-tip-picker) shows under the payment rows only while
  // Online payment is chosen, and the tip goes back to "No tip" when the guest picks
  // something else or leaves the sheet without ordering. Like the delivery fee, the amount
  // and the confirmed choice are the server's (CartService.tip$); a choice is sent through
  // CartService.applyTip() and rides the next cart sync, which Place order waits for.

  tipSettings: TipSettings = parseTipSettings(null);
  selectedTip: TipChoice = { kind: 'none' };
  tipAmount: number = 0;
  tipErrorKey: string = '';
  /**
   * Whether the picker scrolls itself into view when it appears: only when the guest has
   * just tapped Online payment. Not when the sheet opens with Online already chosen (the
   * only method, or restored) — the fields above still need filling in.
   */
  tipRevealOnShow: boolean = false;

  private map: Leaflet.Map | null = null;
  /**
   * True while the map is being moved BY THE CODE (opening on the pin, Back to venue, a
   * looked-up address, a resize). Its `moveend` is not the guest choosing a spot.
   */
  private movingMapProgrammatically = false;
  /** Bumped on every mount/unmount, so a Leaflet load that lands late builds nothing. */
  private mapMountToken = 0;

  /** The map's host exists only while the map is open, so Leaflet loads only then. */
  @ViewChild('deliveryMapHost') set deliveryMapHost(ref: ElementRef<HTMLElement> | undefined) {
    if (ref) {
      this.mountMap(ref.nativeElement);
    } else {
      this.unmountMap();
    }
  }

  constructor(
    private apiService: ApiService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private cartService: CartService
  ) { }

  ngOnInit(): void {
    this.languageService.currentLang$.subscribe(lang => {
      this.currentLang = lang;
    });

    this.currencySymbol = sessionStorage.getItem('currencySymbol') || '';
    this.rememberedPin = readRememberedPin();
    this.subscriptions.push(
      this.cartService.deliveryQuote$.subscribe(quote => { this.deliveryQuote = quote; }),
      this.cartService.deliveryFee$.subscribe(fee => { this.deliveryFee = fee; }),
      this.cartService.total$.subscribe(total => { this.orderTotal = total; }),
      this.cartService.deliveryPricing$.subscribe(pricing => { this.isPricingDelivery = pricing; }),
      this.cartService.deliveryRefused$.subscribe(code => {
        this.deliveryErrorKey = code === 'delivery_not_offered' ? 'notOffered' : 'failed';
      }),
      this.cartService.tip$.subscribe(tip => {
        this.tipAmount = tip?.amount ?? 0;
        this.selectedTip = tipChoiceOf(tip);
        if (tip) {
          this.tipErrorKey = '';
        }
      }),
      // The venue no longer offers what was picked. The cart has already been re-synced
      // without the tip.
      this.cartService.tipRefused$.subscribe(code => {
        this.tipErrorKey = code === 'tip_above_maximum' ? 'aboveMaximum' : 'notAvailable';
      })
    );

    this.loadSavedUserInfo();
    // After the saved user info: the sheet as the guest left it for THIS cart wins.
    this.restoreSheetState();
    this.resolveFulfilmentAndPaymentMethod();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    clearTimeout(this.autoLocateTimer);
    clearTimeout(this.reverseTimer);
    this.unmountMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientData']) {
      this.deliverySettings = parseDeliverySettings(this.clientData);
      this.tipSettings = parseTipSettings(this.clientData);
    }
    if (changes['isOpen'] && this.isOpen) {
      // Opening is not the guest choosing Online, even when it is preselected.
      this.tipRevealOnShow = false;
    }
    if (changes['initialData'] && this.initialData) {
      this.applyInitialData();
    }
    if (changes['isOpen'] && this.isOpen) {
      // Each time the drawer opens, re-derive the delivery type from the current shop config
      // (pickupLocations/clientData are populated by now, unlike at ngOnInit time) — unless
      // the guest already filled this sheet in for the current cart and closed it (to add
      // items, say): then it reopens exactly as they left it, their choice included.
      if (!this.restoreSheetState()) {
        this.userHasChosenType = false;
      }
      this.resolveFulfilmentAndPaymentMethod();

      // If address is read-only but empty, ensure it's loaded
      if (this.isAddressReadOnly && !this.deliveryAddress && this.deliveryData && this.deliveryData.address) {
        this.deliveryAddress = this.deliveryData.address;
      }
    }
    // pickupLocations arrives asynchronously; re-run selection (unless the user already
    // chose) so a pickup-only shop lands on pickup and the single location is preselected.
    if (changes['pickupLocations'] && !this.userHasChosenType) {
      this.resolveFulfilmentAndPaymentMethod();
    }
    /*
     * Re-opened by a refused submit. The guest had chosen delivery, so keep it rather than
     * asking again; the sheet then shows what the server said (the quote's status, or for a
     * missing pin the message below).
     */
    if (changes['submitRefusal'] && this.submitRefusal && this.deliverySettings.enabled) {
      this.deliveryType = 'delivery';
      this.userHasChosenType = true;
      this.autoSelectPaymentMethod();
      this.deliveryErrorKey = this.submitRefusal === 'delivery_location_required' ? 'locationRequired' : '';
    }
    if (changes['isOpen'] && this.isOpen) {
      this.openForDelivery();
    }
    // Every path above may have re-seeded the payment method (or the fulfilment under it).
    // Also takes off a tip left from before the sheet opened when it opens on Cash or Card.
    this.syncTipToPaymentMethod();
  }

  private applyInitialData(): void {
    if (!this.initialData) {
      return;
    }
    
    if (this.initialData.deliveryType) {
      this.deliveryType = this.initialData.deliveryType;
      this.userHasChosenType = true;
    }
    if (this.initialData.deliveryAddress) {
      this.deliveryAddress = this.initialData.deliveryAddress;
    }
    if (this.initialData.pickupLocationId) {
      this.pickupLocationId = this.initialData.pickupLocationId;
    }
    if (this.initialData.phoneNumber) {
      this.phoneNumber = this.initialData.phoneNumber;
    }
    if (this.initialData.countryCode) {
      const savedCountry = this.countries.find(c => c.code === this.initialData!.countryCode);
      if (savedCountry) {
        this.selectedCountryCode = savedCountry;
      }
    }
    if (this.initialData.communicationPreference) {
      this.communicationPreference = this.initialData.communicationPreference;
    }
  }

  private loadSavedUserInfo(): void {
    const savedData = localStorage.getItem('deliveryUserInfo');
    if (savedData) {
      try {
        const userInfo = JSON.parse(savedData);
        this.phone = userInfo.phone || '';
        
        if (userInfo.phoneNumber) {
          this.phoneNumber = userInfo.phoneNumber;
        }
        if (userInfo.countryCode) {
          const savedCountry = this.countries.find(c => c.code === userInfo.countryCode);
          if (savedCountry) {
            this.selectedCountryCode = savedCountry;
          }
        } else if (this.phone) {
          this.parsePhoneNumber(this.phone);
        }
        
        if (!this.isAddressReadOnly) {
          this.deliveryAddress = userInfo.deliveryAddress || '';
        }
        this.pickupLocationId = userInfo.pickupLocationId || '';
        // NOTE: deliveryType is intentionally NOT restored. The chosen delivery method
        // must be derived from the current shop config (autoSelectDeliveryType), not a
        // stale saved preference. Mirrors iOS, which never persists the delivery method.
        this.communicationPreference = userInfo.communicationPreference || 'phone';
      } catch (error) {
        console.error('Error loading saved user info:', error);
      }
    }
  }

  private parsePhoneNumber(fullPhone: string): void {
    for (const country of this.countries) {
      if (fullPhone.startsWith(country.code)) {
        this.selectedCountryCode = country;
        this.phoneNumber = fullPhone.substring(country.code.length).trim();
        return;
      }
    }
    this.phoneNumber = fullPhone;
  }

  /**
   * Decides the delivery type from the shop's enabled fulfillment methods, mirroring iOS
   * (OutsideOrderDetailsViewModel.setupInitialState):
   *  - delivery-with-location flow  -> delivery
   *  - only home delivery enabled   -> delivery
   *  - only in-store pickup enabled -> pickup (preselect the single location)
   *  - BOTH enabled                 -> leave unselected; the user must choose via the toggle
   * Never overrides an explicit user choice. Uses the shop flags as the source of truth,
   * not pickupLocations.length (which may not be loaded yet at first call).
   */
  private autoSelectDeliveryType(): void {
    if (this.userHasChosenType) {
      return;
    }

    // Delivery-with-location flow: the address comes from the scanned location, the type
    // selector is hidden (see the template's !isAddressReadOnly guard) and pickup is not
    // offered — so the type must be forced here, or a shop with BOTH methods enabled would
    // sit at deliveryType === null with no way to choose and a permanently disabled submit.
    // Mirrors iOS (isFormValid skips the method check for .clientWithLocation) and Android
    // (DeliveryTypePopup forces home_delivery for OrderingType.DeliverySpecificLocation).
    if (this.isAddressReadOnly) {
      this.deliveryType = 'delivery';
      return;
    }

    if (hasHomeDelivery(this.clientData) && !hasInStorePickup(this.clientData)) {
      this.deliveryType = 'delivery';
      return;
    }

    if (!hasHomeDelivery(this.clientData) && hasInStorePickup(this.clientData)) {
      this.deliveryType = 'pickup';
      this.preselectSinglePickupLocation();
    } else {
      // Both enabled (or neither flag set yet): no default — force an explicit choice.
      this.deliveryType = null;
      // Still preselect the single pickup location so it's ready if the user picks pickup.
      this.preselectSinglePickupLocation();
    }
  }

  private preselectSinglePickupLocation(): void {
    if (this.pickupLocations.length === 1 && !this.pickupLocationId) {
      this.pickupLocationId = this.pickupLocations[0].id;
    }
  }

  closeForm(): void {
    this.clearErrors();
    this.saveSheetState();
    // Leaving without ordering takes the tip back off — it was chosen for this payment. The
    // method itself is kept (saveSheetState), so reopening shows Online again, with No tip.
    this.clearTip();
    this.formCancel.emit();
  }

  // ── The sheet across closes ─────────────────────────────────────────────
  //
  // Closing the sheet to add items (for free delivery, say) must not cost the guest what
  // they filled in. The order-details screen — and this component with it — is destroyed
  // when they go back to the menu, so the state is kept in storage, tied to the cart's
  // order id: a new cart has a new id, and its sheet starts fresh.

  private saveSheetState(): void {
    const orderId = this.cartService.getCurrentOrderId();
    if (!orderId) return;

    const state: SheetState = {
      orderId,
      deliveryType: this.deliveryType,
      userHasChosenType: this.userHasChosenType,
      deliveryAddress: this.deliveryAddress,
      pickupLocationId: this.pickupLocationId,
      phoneNumber: this.phoneNumber,
      countryCode: this.selectedCountryCode.code,
      communicationPreference: this.communicationPreference,
      paymentMethod: this.paymentMethod,
      deliveryPin: this.deliveryPin,
      pinAddress: this.pinAddress,
      pinSource: this.pinSource,
      sentAddress: this.sentAddress,
      sentPin: this.sentPin,
      addressNotFound: this.addressNotFound,
    };
    try {
      localStorage.setItem(SHEET_STATE_KEY, JSON.stringify(state));
    } catch {
      /* best effort — without it the sheet simply starts from the saved user info */
    }
  }

  /** Put the sheet back as it was for the current cart. False when there is nothing to restore. */
  private restoreSheetState(): boolean {
    let state: SheetState | null = null;
    try {
      state = JSON.parse(localStorage.getItem(SHEET_STATE_KEY) || 'null');
    } catch {
      state = null;
    }
    const orderId = this.cartService.getCurrentOrderId();
    if (!state || !orderId || state.orderId !== orderId) {
      // Another cart's sheet: forget it.
      if (state) {
        try { localStorage.removeItem(SHEET_STATE_KEY); } catch { /* best effort */ }
      }
      return false;
    }

    this.deliveryType = state.deliveryType ?? null;
    this.userHasChosenType = !!state.userHasChosenType && !!state.deliveryType;
    if (!this.isAddressReadOnly) {
      this.deliveryAddress = state.deliveryAddress || '';
    }
    this.pickupLocationId = state.pickupLocationId || '';
    this.phoneNumber = state.phoneNumber || '';
    const country = this.countries.find(c => c.code === state!.countryCode);
    if (country) {
      this.selectedCountryCode = country;
    }
    this.communicationPreference = state.communicationPreference || 'phone';
    this.paymentMethod = state.paymentMethod || '';
    this.deliveryPin = parseLatLng(state.deliveryPin);
    this.pinAddress = state.pinAddress ?? null;
    this.pinSource = state.pinSource ?? null;
    this.sentAddress = state.sentAddress ?? null;
    this.sentPin = parseLatLng(state.sentPin);
    this.addressNotFound = !!state.addressNotFound && !!this.deliveryPin;
    // The map reopens only where it is required; otherwise it is one tap away.
    this.isMapOpen = this.addressNotFound;
    this.pinMovedOnMap = false;
    this.mapAddress = null;
    return true;
  }

  private clearErrors(): void {
    this.phoneError = '';
    this.isPhoneValid = false;
    this.addressError = '';
    this.pickupLocationError = '';
  }

  /**
   * Calculate minimum phone number length from phone format
   * Counts the number of '#' characters in the format string
   */
  private getMinPhoneLength(countryCode: CountryCode): number {
    if (!countryCode.phoneFormat) {
      return 6; // fallback minimum
    }
    return (countryCode.phoneFormat.match(/#/g) || []).length;
  }

  validatePhone(showErrors: boolean = true): boolean {
    if (showErrors) {
      this.phoneError = '';
    }
    this.isPhoneValid = false;
    if (!this.phoneNumber || this.phoneNumber.trim() === '') {
      if (showErrors) {
        this.phoneError = this.translate.instant('orderDetails.deliveryForm.errors.phoneRequired');
      }
      return false;
    }
    const phoneRegex = /^[\d\s\+\-\(\)]+$/;
    if (!phoneRegex.test(this.phoneNumber)) {
      if (showErrors) {
        this.phoneError = this.translate.instant('orderDetails.deliveryForm.errors.phoneInvalid');
      }
      return false;
    }
    const digitsOnly = this.phoneNumber.replace(/\D/g, '');
    const minLength = this.getMinPhoneLength(this.selectedCountryCode);
    
    if (digitsOnly.length < minLength) {
      if (showErrors) {
        this.phoneError = this.translate.instant(
          'orderDetails.deliveryForm.errors.phoneTooShort', { min: minLength });
      }
      return false;
    }
    if (digitsOnly.length > this.selectedCountryCode.maxLength) {
      if (showErrors) {
        this.phoneError = this.translate.instant(
          'orderDetails.deliveryForm.errors.phoneTooLong', { max: this.selectedCountryCode.maxLength });
      }
      return false;
    }
    this.phone = this.getFullPhoneNumber();
    this.isPhoneValid = true;
    return true;
  }

  validateAddress(): boolean {
    this.addressError = '';
    if (this.deliveryType === 'delivery') {
      const addressToValidate = this.isAddressReadOnly && this.deliveryData?.address 
        ? this.deliveryData.address 
        : this.deliveryAddress;
      
      if (!addressToValidate || addressToValidate.trim() === '') {
        this.addressError = this.translate.instant('orderDetails.deliveryForm.errors.addressRequired');
        return false;
      }
    }
    return true;
  }

  validatePickupLocation(): boolean {
    this.pickupLocationError = '';
    if (this.deliveryType === 'pickup') {
      const effectivePickupLocationId = this.pickupLocationId || 
        (this.pickupLocations.length === 1 ? this.pickupLocations[0].id : '');
      
      if (!effectivePickupLocationId || effectivePickupLocationId.trim() === '') {
        this.pickupLocationError = this.translate.instant('orderDetails.deliveryForm.errors.pickupLocationRequired');
        return false;
      }
    }
    return true;
  }

  validateForm(): boolean {
    // A delivery method must be chosen (relevant when both delivery and pickup are offered).
    if (!this.deliveryType) {
      return false;
    }
    const phoneValid = this.validatePhone();
    const addressValid = this.validateAddress();
    const pickupValid = this.validatePickupLocation();
    return phoneValid && addressValid && pickupValid;
  }

  onDeliveryTypeChange(): void {
    if (this.isAddressReadOnly) {
      this.deliveryType = 'delivery';
      return;
    }
    // The user has now explicitly picked a method; don't let reactive re-runs override it.
    this.userHasChosenType = true;
    this.clearErrors();
    if (this.deliveryType === 'pickup') {
      this.deliveryAddress = '';
    }
    if (this.deliveryType === 'pickup' && this.pickupLocations.length === 1) {
      this.pickupLocationId = this.pickupLocations[0].id;
    }
    this.autoSelectPaymentMethod();
    this.onFulfilmentChangedForDelivery();
    // Switching fulfilment can swap Online for a method the other list offers instead.
    this.syncTipToPaymentMethod();
  }

  // ── Delivery pricing ────────────────────────────────────────────────────

  /** True when this sheet has to price the delivery before the order can be placed. */
  get usesDeliveryPricing(): boolean {
    return this.deliverySettings.enabled && this.deliveryType === 'delivery';
  }

  /** The address the courier reads: the typed one, or the predefined location's. */
  get effectiveAddress(): string {
    return (this.isAddressReadOnly && this.deliveryData?.address
      ? this.deliveryData.address
      : this.deliveryAddress) || '';
  }

  /**
   * The pin and address on screen are what the order has. Decided by the SERVER's location,
   * not by "we sent something": a refused or failed sync leaves this false, and so does a
   * new cart, which starts without a location.
   */
  get isLocationConfirmed(): boolean {
    return !!this.deliveryPin
      && samePin(this.deliveryQuote?.location, this.deliveryPin)
      && this.sentAddress === this.effectiveAddress.trim();
  }

  /** Place order is allowed: the server has priced THIS location and accepts it. */
  get canPlaceDeliveryOrder(): boolean {
    return this.isLocationConfirmed && !this.isPricingDelivery && this.deliveryQuote?.status === 'ok';
  }

  /** The pin on screen is the one for the address on screen. */
  get hasPinForAddress(): boolean {
    return !!this.deliveryPin && this.pinAddress === this.effectiveAddress.trim();
  }

  /** The out-of-range answer may be the geocoder's mistake, not the guest's address. */
  get suggestAdjustingPin(): boolean {
    return this.pinSource === 'geocoded' && !this.isMapOpen
      && this.isLocationConfirmed && this.deliveryQuote?.status === 'out_of_range';
  }

  /**
   * The typed address changed. Looked up once the guest stops typing — never per keystroke
   * (Nominatim's policy forbids type-ahead), and geocodeAddress() holds every lookup to
   * its rate limit on top of this.
   */
  onDeliveryAddressInput(): void {
    this.scheduleAutoLocate(1500);
  }

  /**
   * "Adjust on map": open the map on the CURRENT pin, to drag it and confirm.
   *
   * ⚠️ Read at open time, not when the map was first built: iOS had a bug where the start
   * position was a stale copy and the map opened on the venue. A map that is still mounted
   * is moved to the pin here; a new one reads `deliveryPin` when it is built (mountMap()).
   */
  openDeliveryMap(): void {
    this.isMapOpen = true;
    this.resetMapMove();

    /*
     * Available from the start, even before any address is typed: with no pin yet, the map
     * opens on the venue. That start point is a pin to confirm like any other, but it does
     * NOT fill the address field — only a confirmed pin does (confirmDeliveryLocation()).
     */
    const venue = this.deliverySettings.venue;
    if (!this.deliveryPin && venue) {
      this.deliveryPin = { lat: venue.lat, lng: venue.lng };
      this.pinAddress = this.effectiveAddress.trim();
      this.pinSource = 'manual';
    }

    const pin = this.deliveryPin;
    if (pin) {
      this.setMapView(pin, 17);
    }
    // An empty field is filled from the pin on Confirm, moved or not — so the start point is
    // looked up too, for the header.
    if (pin && this.isAddressFieldEmpty) {
      this.lookUpPinAddress(pin);
    }
  }

  /** No address typed (and none predefined): Confirm fills it from the pin. */
  get isAddressFieldEmpty(): boolean {
    return !this.isAddressReadOnly && !this.deliveryAddress.trim();
  }

  /**
   * "Back to venue": centre the map on the venue — a move like any other (looked up, still
   * needing Confirm). Recorded directly rather than through `moveend`, which does not fire
   * when the map is already there.
   */
  movePinToVenue(): void {
    const venue = this.deliverySettings.venue;
    if (!venue) return;
    this.setMapView(venue, 16);
    this.movePinByHand({ lat: venue.lat, lng: venue.lng });
  }

  /** Move the map (and so the centre pin) without it counting as the guest's move. */
  private setMapView(pin: LatLng, zoom: number): void {
    if (!this.map) return;
    // Unanimated, so its `moveend` fires inside this flag rather than after it.
    this.movingMapProgrammatically = true;
    try {
      this.map.setView([pin.lat, pin.lng], zoom, { animate: false });
    } finally {
      this.movingMapProgrammatically = false;
    }
  }

  /** The text the map's header shows: the address at a moved pin, or the typed one. */
  get mapHeaderAddress(): string {
    return this.pinMovedOnMap || this.isAddressFieldEmpty ? (this.mapAddress ?? '') : this.effectiveAddress.trim();
  }

  /** Send the pin and the address to the order. A cart update, not the submit. */
  confirmDeliveryLocation(): void {
    if (!this.deliveryPin) return;

    // The address found at the pin becomes the courier's text when the guest moved the pin,
    // or when the field is empty (then even unmoved — confirming the venue start point).
    // Not for a predefined location, whose address is not the guest's to change; and when
    // nothing was found at the pin, the typed text stays.
    if (this.mapAddress && (this.pinMovedOnMap || this.isAddressFieldEmpty) && !this.isAddressReadOnly) {
      this.deliveryAddress = this.mapAddress;
      this.addressError = '';
    }
    this.resetMapMove();

    // Sent and priced even with no address yet (it is the pin that is priced); the order
    // still needs an address before submit, which isFormValid() keeps requiring.
    const address = this.effectiveAddress.trim();
    const location: DeliveryLocation = { lat: this.deliveryPin.lat, lng: this.deliveryPin.lng, address };
    this.pinAddress = address;
    this.sentAddress = address;
    this.sentPin = { lat: location.lat, lng: location.lng };
    this.addressNotFound = false;
    this.deliveryErrorKey = '';
    this.remember(location);
    this.cartService.applyDeliveryLocation(location);
  }

  /** The amount in a message, e.g. "2.00 €". The figure is the server's; this only formats it. */
  formatDeliveryAmount(amount: number | null): string {
    return `${(amount ?? 0).toFixed(2)} ${this.currencySymbol}`;
  }

  /**
   * The sheet opened. Before anything is looked up, ask the server where this order is
   * already going: after a reload the cart knows the fee but not the pin, and a guess from
   * the geocoder must never overwrite a pin the guest placed by hand.
   */
  private openForDelivery(): void {
    if (!this.deliverySettings.enabled) return;
    if (!this.deliveryQuote && !this.isPricingDelivery) {
      this.serverLocation = this.cartService.fetchDeliveryLocation();
    }
    this.scheduleAutoLocate(0);
  }

  /**
   * Delivery or pickup was just picked. Pickup takes the location (and its fee) off the
   * order; delivery puts one back, or looks the address up.
   */
  private onFulfilmentChangedForDelivery(): void {
    if (!this.deliverySettings.enabled) return;

    if (this.deliveryType === 'pickup') {
      // The typed address was just cleared above, so the pin that went with it goes too.
      clearTimeout(this.autoLocateTimer);
      this.deliveryPin = null;
      this.pinAddress = null;
      this.pinSource = null;
      this.sentAddress = null;
      this.sentPin = null;
      this.isMapOpen = false;
      this.addressNotFound = false;
      this.resetMapMove();
      this.deliveryErrorKey = '';
      if (this.deliveryQuote || this.deliveryFee !== null) {
        this.cartService.applyDeliveryLocation(null);
      }
      return;
    }

    this.scheduleAutoLocate(0);
  }

  private scheduleAutoLocate(delayMs: number): void {
    clearTimeout(this.autoLocateTimer);
    if (!this.usesDeliveryPricing) return;
    this.autoLocateTimer = setTimeout(() => this.autoLocate(), delayMs);
  }

  /**
   * Make sure the order has a location for the address on screen, cheapest source first:
   *
   *   1. the pin is already for this address — re-send it only if the guest sent it before
   *      and the order lost it (a new cart); a pin being dragged waits for Confirm;
   *   2. the order's own location from the server, when it was set for this address —
   *      adopted as confirmed, nothing re-sent;
   *   3. the pin this browser remembers for this address — sent;
   *   4. one Nominatim lookup — sent; or, when nothing is found, the pin at the venue and
   *      the map open for the guest to place it.
   */
  private async autoLocate(): Promise<void> {
    if (!this.usesDeliveryPricing || this.isGeocoding) return;
    const address = this.effectiveAddress.trim();
    if (!address) return;

    if (this.hasPinForAddress) {
      // After a reload the order's answer may still be on its way (openForDelivery()); a
      // pin the server already has must not be sent again.
      await this.serverLocation;
      if (this.hasPinForAddress && !this.isLocationConfirmed && !this.isPricingDelivery
          && this.sentAddress === address && samePin(this.sentPin, this.deliveryPin)) {
        this.confirmDeliveryLocation();
      }
      return;
    }

    this.isGeocoding = true;
    try {
      const server = await this.serverLocation;
      if (this.effectiveAddress.trim() !== address || !this.usesDeliveryPricing) return;

      if (server && server.address.trim() === address) {
        this.usePin(server, address, 'server');
        if (samePin(this.deliveryQuote?.location, server)) {
          // Already on the order and priced: confirmed as it stands.
          this.sentAddress = address;
          this.sentPin = { lat: server.lat, lng: server.lng };
          this.remember({ ...server, address });
        } else {
          // Known only from the order meta (no priced answer yet): send it to be priced.
          this.confirmDeliveryLocation();
        }
        return;
      }

      if (this.rememberedPin && this.rememberedPin.address === address) {
        this.usePin(this.rememberedPin, address, 'remembered');
        this.confirmDeliveryLocation();
        return;
      }

      const found = await geocodeAddress(address, this.deliverySettings, this.currentLang);
      // The guest kept typing (or switched to pickup) while the lookup ran: it is stale.
      if (this.effectiveAddress.trim() !== address || !this.usesDeliveryPricing) return;

      if (found) {
        this.usePin(found, address, 'geocoded');
        this.confirmDeliveryLocation();
      } else if (this.deliverySettings.venue) {
        this.usePin(this.deliverySettings.venue, address, 'manual');
        this.addressNotFound = true;
        this.isMapOpen = true;
      }
    } finally {
      this.isGeocoding = false;
      // Typing during the lookup was ignored by the guard above; look the new text up now.
      if (this.effectiveAddress.trim() !== address) {
        this.scheduleAutoLocate(1500);
      }
    }
  }

  private usePin(pin: LatLng, address: string, source: 'geocoded' | 'manual' | 'remembered' | 'server'): void {
    this.deliveryPin = { lat: pin.lat, lng: pin.lng };
    this.pinAddress = address;
    this.pinSource = source;
    this.addressNotFound = false;
    this.deliveryErrorKey = '';
    this.setMapView(pin, 17);
  }

  /**
   * The guest moved the map under the centre pin (or sent it back to the venue). Sent only
   * when they confirm it. The address there is looked up once the map has settled —
   * `moveend`, never during the pan, plus a short debounce for a run of pans and zooms —
   * and shown in the map's header.
   */
  private movePinByHand(pin: LatLng): void {
    this.deliveryPin = pin;
    this.pinAddress = this.effectiveAddress.trim();
    this.pinSource = 'manual';
    this.pinMovedOnMap = true;
    this.lookUpPinAddress(pin);
  }

  /** One reverse lookup for the pin once it has settled; the newest move wins. */
  private lookUpPinAddress(pin: LatLng): void {
    this.mapAddress = null;
    this.isReverseGeocoding = true;

    clearTimeout(this.reverseTimer);
    const token = ++this.reverseToken;
    this.reverseTimer = setTimeout(async () => {
      const found = await reverseGeocode(pin, this.currentLang);
      if (token !== this.reverseToken) return;
      this.mapAddress = found;
      this.isReverseGeocoding = false;
    }, 600);
  }

  /** Forget a move's pending lookup and its address. */
  private resetMapMove(): void {
    clearTimeout(this.reverseTimer);
    this.reverseToken++;
    this.pinMovedOnMap = false;
    this.mapAddress = null;
    this.isReverseGeocoding = false;
  }

  /** Keep the pin for this address in this browser, so the next order can reuse it. */
  private remember(location: DeliveryLocation): void {
    this.rememberedPin = location;
    try {
      localStorage.setItem(REMEMBERED_PIN_KEY, JSON.stringify(location));
    } catch {
      /* best effort — a denied localStorage only means the next order looks it up again */
    }
  }

  /**
   * Build the map: OpenStreetMap tiles, the venue, and its areas as circles (the server's
   * are what count — these only show the guest roughly where delivery reaches).
   *
   * The guest's pin is NOT a map layer. It is fixed over the centre of the map (an overlay in
   * the template), and the guest moves the map underneath it — as on iOS and Android, because
   * dragging a small marker, or tapping to place one, is fiddly on a phone. The pin's
   * coordinate is therefore always `map.getCenter()`, and every zoom is around the centre so
   * a pinch never slides the spot out from under it.
   */
  private async mountMap(host: HTMLElement): Promise<void> {
    const token = ++this.mapMountToken;
    const venue = this.deliverySettings.venue;
    if (!venue) return;

    let L: Awaited<ReturnType<typeof loadLeaflet>>;
    try {
      L = await loadLeaflet();
    } catch {
      this.deliveryErrorKey = 'mapUnavailable';
      return;
    }
    if (token !== this.mapMountToken || !host.isConnected) return;

    // Read now, at build time — the current pin, never a copy taken when the map was asked for.
    const pin = this.deliveryPin ?? venue;
    const accent = getComputedStyle(host).getPropertyValue('--menu-accent').trim() || '#FF6064';

    const options: Leaflet.MapOptions & { tap?: boolean } = {
      touchZoom: 'center',
      scrollWheelZoom: 'center',
      doubleClickZoom: 'center',
      // Leaflet's legacy tap handler only adds a delay here: nothing on this map is tapped.
      tap: false,
    };
    const map = L.map(host, options)
      .setView([pin.lat, pin.lng], this.deliveryPin && !this.addressNotFound ? 17 : 14);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    // Largest first, so the smaller (cheaper) areas draw on top.
    [...this.deliverySettings.areas].reverse().forEach(area => {
      L.circle([venue.lat, venue.lng], {
        radius: area.radiusKm * 1000,
        color: accent,
        weight: 1,
        fillOpacity: 0.06,
        interactive: false,
      }).addTo(map);
    });
    L.circleMarker([venue.lat, venue.lng], {
      radius: 6,
      color: '#FFFFFF',
      weight: 2,
      fillColor: accent,
      fillOpacity: 1,
      interactive: false,
    }).addTo(map);

    // The map settled after the guest moved it: its centre is the candidate pin.
    map.on('moveend', () => {
      if (this.movingMapProgrammatically) return;
      const centre = map.getCenter();
      this.movePinByHand({ lat: centre.lat, lng: centre.lng });
    });

    this.map = map;

    // The sheet slides in; let Leaflet measure the container once it has its final size.
    // Keeping the centre where it was, and not as a move of the guest's.
    setTimeout(() => {
      this.movingMapProgrammatically = true;
      try {
        this.map?.invalidateSize({ pan: false });
        if (this.map && this.deliveryPin) {
          this.map.setView([this.deliveryPin.lat, this.deliveryPin.lng], this.map.getZoom(), { animate: false });
        }
      } finally {
        this.movingMapProgrammatically = false;
      }
    }, 350);
  }

  private unmountMap(): void {
    this.mapMountToken++;
    this.map?.remove();
    this.map = null;
  }

  /**
   * Work out the fulfilment, then re-check the payment method against it.
   *
   * ⚠️ These two ALWAYS go together, which is why they are behind one call.
   * `autoSelectDeliveryType()` changes `deliveryType` on four of its five paths, and
   * every one of those changes which methods the venue offers — home delivery and
   * in-store pickup carry separate lists. A type resolved without re-seeding leaves
   * `paymentMethod` holding a value from the other list, or '', while the picker shows
   * the new one: a picker with no row highlighted, and an order submitted with no
   * payment method against it (isFormValid() does not require one, and onSubmitForm()
   * sends `paymentMethod || undefined`).
   *
   * It was exactly that: ngOnInit called both, but the two ngOnChanges paths that
   * resolve the type — the drawer opening, and pickupLocations arriving late — called
   * only the first. iOS keeps the same invariant by re-checking on every change of
   * availablePaymentMethods (OutsideOrderDetailsSheet), rather than on the tap.
   */
  private resolveFulfilmentAndPaymentMethod(): void {
    this.autoSelectDeliveryType();
    this.autoSelectPaymentMethod();
  }

  private autoSelectPaymentMethod(): void {
    const methods = this.getAvailablePaymentMethods();
    if (methods.length === 1) {
      this.paymentMethod = methods[0];
    } else if (methods.length > 1 && !methods.includes(this.paymentMethod)) {
      this.paymentMethod = methods[0];
    } else if (methods.length === 0) {
      this.paymentMethod = '';
    }
  }

  onSubmitForm(): void {
    if (this.deliveryType === 'pickup' && this.pickupLocations.length === 1 && !this.pickupLocationId) {
      this.pickupLocationId = this.pickupLocations[0].id;
    }
    
    if (this.validateForm() && this.deliveryType) {
      this.saveUserInfo();
      // Kept in case the server refuses the submit and the sheet comes back.
      this.saveSheetState();

      const formData: DeliveryFormData = {
        deliveryType: this.deliveryType,
        deliveryAddress: this.deliveryAddress,
        pickupLocationId: this.pickupLocationId,
        phone: this.getFullPhoneNumber(),
        phoneNumber: this.phoneNumber,
        countryCode: this.selectedCountryCode.code,
        communicationPreference: this.communicationPreference,
        paymentMethod: this.paymentMethod || undefined
      };

      const selectedLocation = this.pickupLocations.find(loc => loc.id === this.pickupLocationId);
      if (selectedLocation) {
        formData.pickupLocationName = selectedLocation.name;
        formData.pickupLocationAddress = selectedLocation.address;
      }

      this.formSubmit.emit(formData);
    }
  }

  private saveUserInfo(): void {
    const fullPhone = this.getFullPhoneNumber();
    const userInfo = {
      phone: fullPhone,
      phoneNumber: this.phoneNumber,
      countryCode: this.selectedCountryCode.code,
      deliveryAddress: this.deliveryAddress,
      pickupLocationId: this.pickupLocationId,
      // deliveryType intentionally not persisted (see loadSavedUserInfo).
      communicationPreference: this.communicationPreference
    };
    localStorage.setItem('deliveryUserInfo', JSON.stringify(userInfo));
  }

  getFullPhoneNumber(): string {
    if (!this.phoneNumber) {
      return '';
    }
    return `${this.selectedCountryCode.code}${this.phoneNumber}`;
  }

  onCountryCodeChange(): void {
    if (this.phoneNumber) {
      this.validatePhone();
    }
  }

  openCountrySelector(): void {
    this.isCountrySelectorOpen = true;
    this.countrySearchQuery = '';
    this.filteredCountries = this.countries;
  }

  closeCountrySelector(): void {
    this.isCountrySelectorOpen = false;
    this.countrySearchQuery = '';
    this.filteredCountries = this.countries;
  }

  selectCountry(country: CountryCode): void {
    this.selectedCountryCode = country;
    this.onCountryCodeChange();
    this.closeCountrySelector();
  }

  filterCountries(): void {
    if (!this.countrySearchQuery || this.countrySearchQuery.trim() === '') {
      this.filteredCountries = this.countries;
      return;
    }

    const query = this.countrySearchQuery.toLowerCase().trim();
    this.filteredCountries = this.countries.filter(country =>
      country.name.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query) ||
      country.flag.includes(query)
    );
  }

  toggleCommunicationDropdown(): void {
    this.isCommunicationDropdownOpen = !this.isCommunicationDropdownOpen;
  }

  closeCommunicationDropdown(): void {
    this.isCommunicationDropdownOpen = false;
  }

  selectCommunicationPreference(preference: 'phone' | 'whatsapp' | 'viber' | 'telegram'): void {
    this.communicationPreference = preference;
    this.closeCommunicationDropdown();
  }

  getCommunicationLogo(preference: 'phone' | 'whatsapp' | 'viber' | 'telegram'): string {
    const logos: Record<string, string> = {
      'phone': '../../../assets/phone.fill.svg',
      'whatsapp': '../../../assets/message.fill.svg',
      'viber': '../../../assets/message.fill.svg',
      'telegram': '../../../assets/paperplane.fill.svg'
    };
    return logos[preference] || logos['phone'];
  }

  getCommunicationLabel(preference: 'phone' | 'whatsapp' | 'viber' | 'telegram'): string {
    const labels: Record<string, string> = {
      'phone': 'orderDetails.deliveryForm.communication.phone',
      'whatsapp': 'orderDetails.deliveryForm.communication.whatsapp',
      'viber': 'orderDetails.deliveryForm.communication.viber',
      'telegram': 'orderDetails.deliveryForm.communication.telegram'
    };
    return labels[preference] || labels['phone'];
  }

  getCommunicationOptions(): ('phone' | 'whatsapp' | 'viber' | 'telegram')[] {
    return ['phone', 'whatsapp', 'viber', 'telegram'];
  }

  shouldShowDeliveryTypeSelector(): boolean {
    return hasHomeDelivery(this.clientData) && hasInStorePickup(this.clientData);
  }

  /** Template helper — the header asks the guest to choose only when both methods are offered. */
  offersBothFulfillmentMethods(): boolean {
    return this.shouldShowDeliveryTypeSelector();
  }

  getAvailablePaymentMethods(): string[] {
    /*
     * ⚠️ Nothing is on offer until the guest has said how they want the order.
     *
     * The ternary below has only two outcomes, so without this guard a null
     * deliveryType silently reads as pickup and hands back a list for a fulfilment
     * nobody chose — which autoSelectPaymentMethod() then seeds from. iOS returns []
     * for the same state (OutsideOrderDetailsViewModel.availablePaymentMethods,
     * `case nil`).
     */
    if (this.deliveryType === null) {
      return [];
    }

    const methods = this.deliveryType === 'delivery'
      ? this.clientData?.home_delivery_payment_methods
      : this.clientData?.pickup_in_store_payment_methods;
    return Array.isArray(methods) ? methods : [];
  }

  /**
   * Which wording the rows use — or null while the guest has not said how they want the
   * order yet.
   *
   * ⚠️ `deliveryType` starts null whenever the venue offers both, and there is nothing
   * to label at that point: "Cash at pickup" and "Cash on delivery" are different
   * promises, and guessing one is worse than showing none. iOS guards the same case by
   * making `PaymentFulfilment(orderType:)` a failable init.
   *
   * Do NOT collapse this into `deliveryType === 'delivery' ? 'delivery' : 'pickup'` —
   * that quietly captions the unchosen state as pickup.
   */
  get paymentFulfilment(): PaymentFulfilment | null {
    if (this.deliveryType === 'delivery') return 'delivery';
    if (this.deliveryType === 'pickup') return 'pickup';
    return null;
  }

  private paymentRowsCache: Array<PaymentMethodCopy & { method: string }> = [];
  private paymentRowsKey = '';

  /**
   * The rows the picker renders, worded for the chosen fulfilment. Empty while there is
   * no fulfilment to word them for, which is also what hides the section.
   *
   * Built here rather than resolved per binding so the template reads each row's copy
   * once instead of recomputing it for the title, the *ngIf and the subtitle.
   *
   * ⚠️ Memoised on fulfilment + method list, for the same reason `getDineInPaymentOptions()`
   * is in order-details. This is a getter bound to `*ngFor`, so it runs on EVERY change
   * detection pass — including the one a click triggers. Fresh object literals each time
   * are new identities to NgForOf, which then destroys and rebuilds every row underneath
   * the guest's finger. The previous markup iterated the raw string array from
   * `clientData`, whose items are identity-stable, so this hazard arrived with the rows.
   */
  get paymentRows(): Array<PaymentMethodCopy & { method: string }> {
    const fulfilment = this.paymentFulfilment;
    if (fulfilment === null) {
      this.paymentRowsKey = '';
      this.paymentRowsCache = [];
      return this.paymentRowsCache;
    }

    const methods = this.getAvailablePaymentMethods();
    const key = `${fulfilment}|${methods.join('|')}`;

    if (key !== this.paymentRowsKey) {
      this.paymentRowsKey = key;
      this.paymentRowsCache = methods.map(method => ({
        method,
        ...paymentMethodCopy(method, fulfilment),
      }));
    }

    return this.paymentRowsCache;
  }

  /**
   * Shown whenever the venue accepts something for this fulfilment — one method is
   * still a sentence the guest needs, especially Pay now, which charges them on the
   * next tap.
   */
  shouldShowPaymentMethodPicker(): boolean {
    return this.paymentRows.length > 0;
  }

  /**
   * "Continue to payment" for Pay now, because the next tap opens BORICA's card form
   * rather than finishing the order.
   *
   * ⚠️ Held back until a fulfilment is chosen. `autoSelectPaymentMethod()` seeds
   * `paymentMethod` from the PICKUP list while `deliveryType` is still null (that is
   * what `getAvailablePaymentMethods()` falls back to), so a venue whose pickup list
   * starts with online payment would otherwise promise a card form off a choice the
   * guest has not made — next to a hidden picker and a disabled button.
   */
  get submitLabelKey(): string {
    return this.paymentFulfilment === null
      ? 'orderDetails.orderNow.button'
      : commitLabelKey(this.paymentMethod);
  }

  selectPaymentMethod(method: string): void {
    if (method === TIP_PAYMENT_METHOD && this.paymentMethod !== method) {
      // The guest just picked Online, so the picker that appears below is news to them.
      this.tipRevealOnShow = true;
    }
    this.paymentMethod = method;
    this.syncTipToPaymentMethod();
  }

  /** The picker shows under the payment rows only while Online payment is chosen. */
  get showTipPicker(): boolean {
    return this.isOpen && this.tipSettings.enabled && this.tipAllowedForPaymentMethod;
  }

  /**
   * Online payment, chosen for a fulfilment the guest has picked. The fulfilment matters:
   * with none chosen the rows are hidden, and `paymentMethod` may still hold a value seeded
   * off a list the guest never saw (see submitLabelKey).
   */
  private get tipAllowedForPaymentMethod(): boolean {
    return this.paymentFulfilment !== null
      && this.paymentMethod === TIP_PAYMENT_METHOD
      && this.getAvailablePaymentMethods().includes(TIP_PAYMENT_METHOD);
  }

  onTipChosen(choice: TipChoice): void {
    this.tipErrorKey = '';
    if (sameTipChoice(choice, this.selectedTip)) return;
    // Shown at once; the server's answer (CartService.tip$) then confirms or corrects it.
    this.selectedTip = choice;
    this.cartService.applyTip(choice);
  }

  /**
   * Anything but Online payment takes the tip off. Only while the sheet is open: the
   * method is also re-seeded while it is closed (shop data arriving late), and that is
   * not the guest switching away from anything.
   */
  private syncTipToPaymentMethod(): void {
    if (!this.isOpen || this.tipAllowedForPaymentMethod) return;
    this.clearTip();
  }

  /** Back to "No tip". Sends nothing unless a tip is actually on the order. */
  private clearTip(): void {
    this.tipErrorKey = '';
    if (this.selectedTip.kind !== 'none') {
      this.onTipChosen({ kind: 'none' });
    }
  }

  isFormValid(): boolean {
    // Submit stays disabled until the user picks a delivery method (delivery vs pickup).
    if (!this.deliveryType) return false;

    const phoneValid = this.validatePhone(false);
    if (!phoneValid) return false;

    const addressValid = this.validateAddress();
    if (!addressValid) return false;

    const pickupValid = this.validatePickupLocation();
    if (!pickupValid) return false;

    // A priced delivery waits for the server: loading, out of range or below the minimum
    // all keep Place order disabled.
    if (this.usesDeliveryPricing && !this.canPlaceDeliveryOrder) return false;

    return true;
  }
}

/** The sheet as the guest left it, for the cart (order id) it was filled in for. */
const SHEET_STATE_KEY = 'deliverySheetState';

interface SheetState {
  orderId: number;
  deliveryType: 'delivery' | 'pickup' | null;
  userHasChosenType: boolean;
  deliveryAddress: string;
  pickupLocationId: string;
  phoneNumber: string;
  countryCode: string;
  communicationPreference: 'phone' | 'whatsapp' | 'viber' | 'telegram';
  paymentMethod: string;
  deliveryPin: LatLng | null;
  pinAddress: string | null;
  pinSource: 'geocoded' | 'manual' | 'remembered' | 'server' | null;
  sentAddress: string | null;
  sentPin: LatLng | null;
  addressNotFound: boolean;
}

/** The last pin and the address it was set for. Only written on venues that price delivery. */
const REMEMBERED_PIN_KEY = 'deliveryLocation';

function readRememberedPin(): DeliveryLocation | null {
  try {
    const saved = JSON.parse(localStorage.getItem(REMEMBERED_PIN_KEY) || 'null');
    const pin = parseLatLng(saved);
    return pin && typeof saved.address === 'string' ? { ...pin, address: saved.address.trim() } : null;
  } catch {
    return null;
  }
}
